import { PathLayer, PathLayerProps, PolygonLayer, PolygonLayerProps } from "@deck.gl/layers"
import { CompositeLayer, PickingInfo } from "@deck.gl/core"
import { get } from "nodash"
import { Geometry } from "plugins/shared/layers/types"
import {
    FillStyleExtensionProps,
    PathStyleExtension,
    TerrainExtensionProps,
} from "@deck.gl/extensions"
import { convertLineStrokeToValue } from "plugins/Toolbox/Line/module"
import { GeometryType, LineStroke } from "@nova/constants"
import GeometryIconLayer from "./GeometryIconLayer.class"
import { Point } from "@nova/data-create-polygon-kml"
import { Coordinate, Line } from "@nova/protobuf"
import { LngLatCoordinatePair } from "features/map/event/types"
import { getGlobalTerrainData } from "features/map/GlobalTerrainManager"

export type PolygonGeometry = Geometry & { deckGlIdx: number; idx: number }

export default class GeometryLinesLayer<T extends Geometry = Geometry> extends CompositeLayer<{
    current: number

    lineStyles: Partial<PathLayerProps<T>>
    polygonStyles: Partial<
        PolygonLayerProps<T> & FillStyleExtensionProps & TerrainExtensionProps
    > & {
        getStroke?: () => number
    }

    thinLinePoint: LngLatCoordinatePair
    pickable: boolean
    onLayerClick: (geometry: PolygonGeometry) => void
    hoveredIndex: number
    editor: boolean
    getStroke?: () => number
}> {
    state = {} as {
        lineData: PolygonGeometry[]
        iconData: { coordinates: Point[]; url: string }[]
        roundedLineData: PolygonGeometry[]
        polygonData: PolygonGeometry[]
        newLineData: PolygonGeometry
        data: PolygonGeometry[]
        current: number
    }
    static layerName = "geometry_lines"
    updateState({ changeFlags }: { changeFlags: Record<string, unknown> }) {
        const data = get(this.props, "data", {}) as PolygonGeometry[]
        const current = get(this.props, "current") || 0

        if (changeFlags.dataChanged) {
            const lineData: PolygonGeometry[] = []
            const roundedLineData: PolygonGeometry[] = []
            const polygonData: PolygonGeometry[] = []
            const newLineData: PolygonGeometry | undefined = data.find((d) => d.idx === current)
            const iconData: PolygonGeometry[] = []
            data.forEach((e) => {
                if (e.geometryType === GeometryType.Line) {
                    if (get(e, "url")) {
                        iconData.push(e)
                        return
                    }
                    if (
                        this.props.polygonStyles.getStroke?.() === LineStroke.dotted ||
                        get(e, "stroke") === LineStroke.dotted
                    )
                        roundedLineData.push(e)
                    else lineData.push(e)
                } else polygonData.push(e)
            })
            this.setState({
                lineData,
                iconData,
                roundedLineData,
                polygonData,
                newLineData,
                data,
            })
        }
        if (current !== this.state.current) this.setState({ current })
    }

    onClick(info: PickingInfo, event: { srcEvent: PointerEvent; leftButton: boolean }) {
        if (info.object.idx !== this.state.current && event.leftButton) {
            this.props.onLayerClick(info.object)
        }
        return false
    }

    renderLayers() {
        return [
            new PathLayer({
                id: this.props.id + "-path",
                data: this.state.lineData,
                visible: true,
                widthMinPixels: 2,
                widthMaxPixels: 8,
                getPath: (d) => {
                    return d.coordinates.map((e: Coordinate) => [e.longitude, e.latitude, 0])
                },
                miterLimit: 0,
                pickable: this.props.pickable,
                ...this.props.lineStyles,
                getDashArray: (d: Line) => {
                    return convertLineStrokeToValue(
                        this.props.polygonStyles.getStroke?.() || d.stroke
                    )
                },
                widthUnits: "pixels",
                dashJustified: true,
                dashGapPickable: true,
                capRounded: false,
                extensions: [
                    ...getGlobalTerrainData().extensions,
                    new PathStyleExtension({ dash: true }),
                ],
                parameters: { depthTest: false },
            }),
            //the only way to alternate between rounded and not rounded is to use an entirely separate layer....
            new PathLayer({
                id: this.props.id + "-rounded-path",
                data: this.state.roundedLineData,
                visible: true,
                pickable: this.props.pickable,
                widthMinPixels: 2,
                widthMaxPixels: 8,
                getPath: (d) => {
                    return d.coordinates.map((e: Coordinate) => [e.longitude, e.latitude, 0])
                },
                miterLimit: 0,
                ...this.props.lineStyles,

                getDashArray: (d: Line) => {
                    return convertLineStrokeToValue(
                        this.props.polygonStyles.getStroke?.() || d.stroke
                    )
                },
                widthUnits: "pixels",
                dashJustified: true,
                dashGapPickable: true,
                capRounded: true,
                extensions: [
                    ...getGlobalTerrainData().extensions,
                    new PathStyleExtension({ dash: true }),
                ],
                parameters: { depthTest: false },
            }),
            new PolygonLayer({
                id: this.props.id + "-polygons",
                pickable: this.props.pickable,
                data: this.state.polygonData,
                stroked: true,
                filled: true,
                visible: true,
                lineWidthMinPixels: 2,
                lineWidthMaxPixels: 8,
                extensions: [...getGlobalTerrainData().extensions],
                updateTriggers: {
                    getLineWidth: [this.props.hoveredIndex],
                },
                getPolygon: (d: Line) => {
                    return [
                        d.coordinates.map((e: Coordinate) => [e.longitude, e.latitude, 0]),
                        ...(d.holes?.map((hole) =>
                            hole.coordinates.map((point) => [point.longitude, point.latitude, 0])
                        ) || []),
                    ]
                },
                ...this.props.polygonStyles,
                parameters: { depthTest: false },
            }),
            this.props.thinLinePoint &&
                this.state.newLineData &&
                new PathLayer({
                    id: this.props.id + "-thin-path",
                    data: [
                        {
                            ...this.state.newLineData,
                            coordinates: [
                                this.state.newLineData.coordinates.at(-1),
                                {
                                    longitude: this.props.thinLinePoint[0],
                                    latitude: this.props.thinLinePoint[1],
                                },
                            ],
                        },
                    ],
                    visible: true,
                    getPath: (d) => {
                        return d.coordinates.map((e: Coordinate) => [e.longitude, e.latitude, 0])
                    },
                    miterLimit: 0,
                    ...this.props.lineStyles,
                    pickable: false,
                    widthMinPixels: 1,
                    widthMaxPixels: 1,
                    widthScale: 1,
                    widthUnits: "pixels",
                    extensions: [...getGlobalTerrainData().extensions],
                    parameters: { depthTest: false },
                }),
            new GeometryIconLayer({
                id: this.props.id + "-icon",
                data: this.state.iconData,
                visible: true,
                pickable: false,
                iconWidth: 33,
            }),
        ]
    }
}
